/* Simplified content for brevity but ensuring mobile optimizations are applied */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { TableSkeleton } from '../components/ui/Skeleton';
import { cn } from '../lib/utils';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { useRole } from '../hooks/useRole';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  full_name: '',
  student_id_code: '',
  email: '',
  payment_method: '',
  bio: '',
  enroll_subject_id: '',
};

export default function Students() {
  const navigate = useNavigate();
  const { profile: currentProfile, isAdmin } = useRole();
  const [data, setData] = useState({ list: [], subjects: [], loading: true });
  const [ui, setUi] = useState({ search: '', filter: '' });
  const [registerOpen, setRegisterOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, sub] = await Promise.all([
      supabase.from('students').select('*, student_subjects(subjects(name, id))').order('created_at', { ascending: false }),
      supabase.from('subjects').select('*').order('name'),
    ]);
    setData({ list: s.data || [], subjects: sub.data || [], loading: false });
  };

  useEffect(() => { load(); }, []);

  // Subjects visible to the current user (respects assigned_staff_ids)
  const visibleSubjects = data.subjects.filter(s => {
    const ids = s.assigned_staff_ids || [];
    if (!ids.length || isAdmin) return true;
    return ids.includes(currentProfile?.id);
  });

  const filtered = data.list.filter(s =>
    s.full_name.toLowerCase().includes(ui.search.toLowerCase()) &&
    (!ui.filter || s.student_subjects?.some(ss => ss.subjects.id === ui.filter))
  );

  // Generate next STU-YYYY-NNN code based on existing students this year
  const generateIdCode = async () => {
    const year = new Date().getFullYear();
    const { data } = await supabase
      .from('students')
      .select('student_id_code')
      .ilike('student_id_code', `STU-${year}-%`);

    let maxSeq = 0;
    (data || []).forEach(s => {
      const m = (s.student_id_code || '').match(/STU-\d{4}-(\d+)/);
      if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
    });
    return `STU-${year}-${String(maxSeq + 1).padStart(3, '0')}`;
  };

  const openRegister = async () => {
    const idCode = await generateIdCode();
    setForm({ ...EMPTY_FORM, student_id_code: idCode });
    setRegisterOpen(true);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim())       return toast.error('Full name is required');
    if (!form.student_id_code.trim()) return toast.error('Student ID Code is required');

    setSaving(true);
    const l = toast.loading('Registering student...');
    try {
      // Generate the UUID client-side so we never need to SELECT the new row
      // (teachers can't SELECT a student they're not yet enrolled to teach —
      //  using a known ID avoids the post-insert SELECT RLS issue entirely)
      const newId = crypto.randomUUID();

      const payload = {
        id: newId,
        full_name: form.full_name.trim(),
        student_id_code: form.student_id_code.trim(),
        registered_by: currentProfile?.id || null,
      };
      if (form.email.trim())          payload.email          = form.email.trim();
      if (form.payment_method.trim()) payload.payment_method = form.payment_method.trim();
      if (form.bio.trim())            payload.bio            = form.bio.trim();

      // 1. Insert the student
      const { error: stuError } = await supabase
        .from('students')
        .insert([payload]);

      if (stuError) throw stuError;

      // 2. Optionally create the initial subject enrolment.
      //    For teachers: also set themselves as main_teacher_id so the
      //    student appears in their get_my_student_ids() list and they
      //    can access the student profile immediately.
      if (form.enroll_subject_id) {
        const enrollPayload = {
          student_id: newId,
          subject_id: form.enroll_subject_id,
        };
        if (!isAdmin && currentProfile?.id) {
          enrollPayload.main_teacher_id = currentProfile.id;
        }

        const { error: enError } = await supabase
          .from('student_subjects')
          .insert([enrollPayload]);

        if (enError) {
          console.warn('Enrolment insert failed:', enError.message);
          toast.success(
            `${form.full_name} registered! (Enrolment failed — add a subject from their profile)`,
            { id: l, duration: 6000 }
          );
        } else {
          toast.success(`${form.full_name} registered and enrolled!`, { id: l });
        }
      } else {
        toast.success(`${form.full_name} registered!`, { id: l });
      }

      setRegisterOpen(false);
      await load();
      navigate(`/students/${newId}`);
    } catch (err) {
      toast.error(err.message, { id: l });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 mobile-tight">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-4xl font-bold dark:text-white">Directory</h1>
        <Button onClick={openRegister} className="p-2 md:px-8 rounded-xl">
          <SafeIcon icon={Lu.LuPlus} />
          <span className="hidden md:inline ml-2">Register</span>
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={Lu.LuSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            placeholder="Search..."
            value={ui.search}
            onChange={e => setUi({ ...ui, search: e.target.value })}
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-900 rounded-xl border-none shadow-sm text-sm"
          />
        </div>
        <select
          className="bg-white dark:bg-slate-900 px-4 py-3 rounded-xl border-none shadow-sm text-sm outline-none"
          value={ui.filter}
          onChange={e => setUi({ ...ui, filter: e.target.value })}
        >
          <option value="">All Subjects</option>
          {visibleSubjects.map(s => (
            <option key={s.id} value={s.id}>{s.name} ({s.level || 'Std'})</option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-3xl border shadow-sm overflow-hidden">
        {data.loading ? <TableSkeleton /> : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {filtered.length === 0 && (
              <div className="py-16 text-center text-slate-400 text-sm font-medium">
                {ui.search || ui.filter
                  ? 'No students match your search.'
                  : 'No students yet. Click Register to add one.'}
              </div>
            )}
            {filtered.map(s => (
              <div
                key={s.id}
                onClick={() => navigate(`/students/${s.id}`)}
                className="p-4 md:px-8 md:py-5 flex items-center justify-between hover:bg-slate-50 cursor-pointer group transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary-100 flex items-center justify-center font-black text-primary-700 text-sm">
                    {s.full_name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-sm dark:text-white group-hover:text-primary-600">{s.full_name}</p>
                    <div className="flex gap-1 mt-1 truncate max-w-[150px] sm:max-w-none">
                      {s.student_subjects?.map((ss, i) => (
                        <span key={i} className="text-[8px] font-medium text-slate-400">
                          {ss.subjects?.name}{i < s.student_subjects.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-mono text-slate-400">{s.student_id_code}</p>
                  <SafeIcon icon={Lu.LuChevronRight} className="inline ml-2 text-slate-300" size={16} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Register Student Modal */}
      <Modal isOpen={registerOpen} onClose={() => setRegisterOpen(false)} title="Register New Student" className="max-w-lg">
        <form onSubmit={handleRegister} className="p-5 space-y-5">

          {/* Tip for non-admin teachers */}
          {!isAdmin && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 flex items-start gap-2">
              <SafeIcon icon={Lu.LuInfo} size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-700 font-medium leading-relaxed">
                Select an initial subject below so you are assigned as this student's teacher and can access their profile immediately.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Input
                label="Full Name *"
                placeholder="e.g. Jane Smith"
                value={form.full_name}
                onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                label="Student ID Code *"
                placeholder="e.g. STU-001"
                value={form.student_id_code}
                onChange={e => setForm(p => ({ ...p, student_id_code: e.target.value }))}
                required
              />
            </div>
            <Input
              label="Email (optional)"
              type="email"
              placeholder="jane@example.com"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            />
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 px-1 block">Payment Method (optional)</label>
              <select
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                value={form.payment_method}
                onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}
              >
                <option value="">— Select —</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="E-Wallet">E-Wallet</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Initial subject enrolment — required for teachers to see the profile */}
            <div className="sm:col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-500 px-1 block">
                Initial Subject Enrolment
                <span className="ml-1 text-slate-400 font-normal normal-case">{isAdmin ? '(optional)' : '(recommended)'}</span>
              </label>
              <select
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                value={form.enroll_subject_id}
                onChange={e => setForm(p => ({ ...p, enroll_subject_id: e.target.value }))}
              >
                <option value="">— Skip for now —</option>
                {visibleSubjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.level || 'Std'})</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-500 px-1 block">Notes (optional)</label>
              <textarea
                placeholder="Any notes about this student..."
                value={form.bio}
                onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 min-h-[72px]"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="ghost" onClick={() => setRegisterOpen(false)} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-[2] font-bold">
              <SafeIcon icon={Lu.LuUserPlus} size={15} className="mr-2" />
              {saving ? 'Registering...' : 'Register Student'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}